import { useState, useEffect, useMemo } from 'react'
import { useStylesTheme } from '../../config'
import {
    Box,
    Grid,
    Card,
    Toolbar,
    IconButton,
    FormControl,
    Tooltip
} from '@mui/material'
import { ArrowBackIos } from '@mui/icons-material'
import HistoryIcon from '@mui/icons-material/History'
import { useNavigate, useParams } from 'react-router-dom'
import { patternsRoutes, reportsRoutes, machinesRoutes } from '../../routes'
import { getModelandSite, obtenerModeloMaquinas, getMachinesBySite } from '../../routes/machines.routes'
import { createManualReport } from '../../routes/reports.routes'
import './reports.css'
import { useAuth, useReportsContext, useSitesContext, useUser } from '../../context'
import { LoadingLogoDialog, ReportConfirmDialog } from '../../dialogs'
import { useAlertContext } from '../../context/Alert.context'
import { SelectionPopover } from '../../popover/Selection.popover'
import { segundosHoras } from '../../utils/NumberUtils'
import { useStatus } from '../../context/Status.context'

const CreateReports = () => {
    const { setAlertSimple } = useAlertContext()
    const { reports, getReports, getReportsOffline, refreshNumbers } = useReportsContext()
    const { userData } = useAuth()
    const { admin, isOperator, isChiefMachinery, isSapExecutive, isShiftManager, isSuperAdmin, isOperatorInspector, isUniversalInspector, isUniversalChief } = useUser()
    const isUniversal = isUniversalInspector || isUniversalChief
    const hasSiteSelector = admin || isSuperAdmin || isUniversalChief || isUniversalInspector || isShiftManager || isChiefMachinery || isSapExecutive
    const { sitesToSelection, siteSelected } = useSitesContext()
    const [allMachinesAtSite, setAllMachinesAtSite] = useState([])
    const [pautasParaMostrar, setPautas] = useState([])
    const [maquinas, setMaquinas] = useState([])
    const [machineModel, setMachineModel] = useState('')
    const [pauta, setPauta] = useState('')
    const [pautaIndex, setPautaIndex] = useState('')
    const [reportType, setReportType] = useState((isUniversalInspector || isUniversalChief || isOperatorInspector) ? 'Inspección' : 'Seleccione...')
    const [machineSelected, setMachineSelected] = useState('init')
    const [disablePautas, setDisablePautas] = useState(true)
    const [changed, setChanged] = useState(false)
    const [disableMaquinas, setDisableMaquinas] = useState(true)
    const [date, setDate] = useState('')
    const [dateEnd, setDateEnd] = useState('')
    const [hourMeter, setHourMeter] = useState('')
    const [equID, setEqID] = useState('')
    const [toDay, setToDay] = useState('')
    const [sapId, setSapId] = useState('')
    const [selectedUniversalSap, setSelectedUniversalSap] = useState('')
    const [isManualSapInput, setIsManualSapInput] = useState(!(isUniversalInspector || isUniversalChief))
    const [iDPM, setIDPM] = useState('')
    const [idObra, setIdObra] = useState('')
    const [openLoadingLogo, setOpenLoadingLogo] = useState(false)
    const classes = useStylesTheme()
    const navigate = useNavigate()
    const { id } = useParams()
    const [openSelectionMachinery, setOpenSelectionMachinery] = useState(false)
    const [anchorEl, setAnchorEl] = useState(null)
    const { setstatus } = useStatus()

    const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
    const [existingReport, setExistingReport] = useState(null)
    const [reportToSave, setReportToSave] = useState(null)

    const trucks = useMemo(() => {
        if (!allMachinesAtSite || allMachinesAtSite.length === 0) return []

        return allMachinesAtSite.map(machine => {
            const isInspection = reportType === 'Inspección'
            const isMaintenance = reportType === 'Mantención'
            const isBoth = reportType === 'Inspección y Mantención'

            let isDisabled = false
            if (isInspection) {
                isDisabled = !machine.idpminspeccion
            } else if (isMaintenance) {
                isDisabled = !machine.idpmmantencion
            } else if (isBoth) {
                isDisabled = false
            }

            return {
                name: `${machine.equ} / ${machine.brand} ${machine.model}`,
                modelo: machine.model,
                brand: machine.brand,
                disabled: isDisabled,
                machine: machine
            }
        }).filter(truck => !truck.disabled)
    }, [allMachinesAtSite, reportType])

    const sitesToShow = useMemo(() => {
        const activeSites = sitesToSelection.filter(s => s.idObra && s.idObra !== 'none' && s._id !== 'universal')
        if (admin || isSuperAdmin || isUniversalInspector || isUniversalChief) return activeSites

        if (!userData || !userData.obras || !Array.isArray(userData.obras)) return []
        const userObrasRefs = userData.obras.map(o => {
            if (typeof o === 'object' && o !== null) return o.idObra || o.idobra || o._id
            return o
        }).filter(Boolean)
        return activeSites.filter(site =>
            userObrasRefs.some(ref => ref === site.idObra || ref === site._id)
        )
    }, [admin, isSuperAdmin, isUniversalInspector, isUniversalChief, sitesToSelection, userData])

    const sitesToFetch = useMemo(() => {
        if (hasSiteSelector) return idObra ? [idObra] : []
        const ids = sitesToShow.map(s => s.idObra).filter(Boolean)
        return ids
    }, [hasSiteSelector, idObra, sitesToShow])

    const filteredMaquinas = useMemo(() => {
        if (reportType === 'Seleccione...' || reportType === 'Inspección y Mantención') return maquinas
        const isInspection = reportType === 'Inspección'
        return maquinas.filter(maquina => isInspection ? maquina.idpminspeccion : maquina.idpmmantencion)
    }, [maquinas, reportType])

    useEffect(() => {
        if (machineSelected && machineSelected !== 'init') {
            const machineCache = JSON.parse(machineSelected)
            const isInspection = reportType === 'Inspección'
            const isMaintenance = reportType === 'Mantención'

            let hasRequiredPauta = true
            if (isInspection) {
                hasRequiredPauta = !!machineCache.idpminspeccion
            } else if (isMaintenance) {
                hasRequiredPauta = !!machineCache.idpmmantencion
            }

            if (!hasRequiredPauta) {
                // Reset machine selection if it's no longer valid for the new report type
                setMachineSelected('init')
                setEqID('')
                setHourMeter('')
                setIDPM('')
                setPautas([])
                setPauta('')
            } else {
                setPautas([])
                setPauta('')
                getPautas(machineCache)
            }
        }
    }, [machineSelected, reportType])

    const getPautas = async (machineCache) => {
        let idpm
        if (reportType === 'Inspección') {
            idpm = machineCache.idpminspeccion
        } else if (reportType === 'Mantención') {
            idpm = machineCache.idpmmantencion
        } else {
            idpm = machineCache.idpminspeccion || machineCache.idpmmantencion
        }

        setIDPM(idpm)
        if (!idpm) {
            setPautas([])
            return
        }
        const response = await patternsRoutes.getPatternsDetailByIdpm(idpm)
        console.log(response.data)
        setPautas(response.data.data)
    }

    const orderType = (a, b) => {
        if (a.idpm > b.idpm) return 1
        if (a.idpm < b.idpm) return -1
        if (a.typepm > b.typepm) return 1
        if (a.typepm < b.typepm) return -1
    }

    useEffect(() => {
        if (machineModel && sitesToFetch.length > 0) {
            console.log('Triggering getMachine with sites:', sitesToFetch)
            getMachine(machineModel)
        }
    }, [machineModel, JSON.stringify(sitesToFetch)])

    useEffect(() => {
        const fetchModels = async () => {
            if (sitesToFetch.length === 0) {
                setAllMachinesAtSite([])
                return
            }
            try {
                console.log(`Fetching machines for sites: ${sitesToFetch} to get models`)
                const allResponses = await Promise.all(
                    sitesToFetch.map(site => getMachinesBySite(site))
                )
                const siteMachines = allResponses.flat().filter(Boolean)

                if (siteMachines && Array.isArray(siteMachines)) {
                    setAllMachinesAtSite(siteMachines)
                    console.log('Loaded all machines for sites:', siteMachines.length)
                }
            } catch (error) {
                console.error('Error fetching models for sites:', error)
                setAllMachinesAtSite([])
            }
        }

        setMachineModel('')
        setMaquinas([])
        setMachineSelected('init')
        setPautas([])
        setPauta('')
        setIDPM('')
        setHourMeter('')
        setEqID('')

        fetchModels()
    }, [JSON.stringify(sitesToFetch)])

    const settingTypePauta = (type) => {
        setPautaIndex(type)
    }

    const getMachine = async (machineModel) => {
        console.log('getMachine called with:', machineModel, 'sites:', sitesToFetch)
        if (!machineModel || sitesToFetch.length === 0) {
            setMaquinas([])
            return
        }
        try {
            console.log(`Fetching machines with model: ${machineModel} for sites: ${sitesToFetch}`)
            const responses = await Promise.all(
                sitesToFetch.map(site => getModelandSite(machineModel, site))
            )
            const combined = responses.flat().filter(Boolean)
            console.log('Combined machines:', combined)
            setMaquinas(combined)
        } catch (error) {
            console.error('Error fetching machines:', error)
            setMaquinas([])
        }
    }

    const saveReport = async (force = false) => {
        const siteObject = sitesToSelection.find(site => site.idObra === idObra)
        if (!siteObject) {
            setstatus({
                status: 'error',
                message: 'Obra seleccionada no es válida.',
                severity: 'warning',
                title: 'Validación'
            })
            return
        }

        if (!machineSelected || machineSelected === 'init') {
            setstatus({
                status: 'error',
                message: 'Debe seleccionar una máquina.',
                severity: 'warning',
                title: 'Validación'
            })
            return
        }

        if (!date || !dateEnd) {
            setstatus({
                status: 'error',
                message: 'Debe seleccionar las fechas de inicio y término programado.',
                severity: 'warning',
                title: 'Validación'
            })
            return
        }

        let machineObj
        try {
            machineObj = JSON.parse(machineSelected)
        } catch (e) {
            console.error('Error parsing machineSelected:', e)
            setstatus({
                status: 'error',
                message: 'Error al procesar la máquina seleccionada.',
                severity: 'error'
            })
            return
        }

        const report = {
            state: 'Asignar',
            datePrev: Date.parse(date),
            endPrev: Date.parse(dateEnd),
            guide: pauta,
            reportType: reportType,
            maquina: machineObj._id,
            obra: siteObject._id,
            sapId: selectedUniversalSap || sapId,
            idPm: iDPM,
            force: force
        }

        if (
            !report.maquina ||
            !report.obra ||
            !report.sapId ||
            (!report.guide || report.guide === 'Selección no cuenta con pautas.') ||
            (!report.reportType || report.reportType === 'Seleccione...')) {
            setstatus({
                status: 'error',
                message: 'Falta información obligatoria para crear el reporte.',
                severity: 'warning',
                title: 'Información incompleta'
            })
        } else {
            setOpenLoadingLogo(true)
            try {
                const response = await createManualReport(report)
                console.log(response)
                const otNumber = response.data?.pautaCreada?.newIdIndex || response.data?.pautaCreada?.idIndex || ''
                setstatus({
                    status: 'completed',
                    message: `Reporte ${otNumber} creado satisfactoriamente.`,
                    title: 'Éxito',
                    severity: 'success'
                })
                await getReports()
                if (getReportsOffline) {
                    await getReportsOffline()
                }
                navigate('/reports', { replace: true })
            } catch (error) {
                console.error('Error creating manual report:', error)

                if (error.response?.status === 409) {
                    let existing = error.response?.data?.existingReport || error.response?.data?.existing || error.response?.data?.report

                    if (!existing && sapId) {
                        const allLocal = reports || []
                        existing = allLocal.find(r => String(r.sapId) === String(sapId))
                    }

                    if (!existing && sapId) {
                        try {
                            const resDeleted = await reportsRoutes.reportesPaginados('todos', 0, 10, 'all', null, null, -1, true)
                            if (resDeleted && resDeleted.reportes) {
                                existing = resDeleted.reportes.find(r => String(r.sapId) === String(sapId))
                            }
                        } catch (e) {
                            console.error('Error buscando reporte eliminado por sapId:', e)
                        }
                    }

                    if (!existing) {
                        existing = {
                            sapId: sapId,
                            deleted: true,
                            state: 'En Papelera',
                            newIdIndex: sapId
                        }
                    }

                    const siteObject = sitesToSelection.find(site => site.idObra === idObra)
                    let machineObj = {}
                    try {
                        machineObj = JSON.parse(machineSelected)
                    } catch (e) {
                        console.error('Error parsing machineSelected:', e)
                    }
                    const populatedReport = {
                        state: 'Asignar',
                        datePrev: Date.parse(date),
                        endPrev: Date.parse(dateEnd),
                        guide: pauta,
                        reportType: reportType,
                        maquina: machineObj,
                        obra: siteObject,
                        sapId: sapId,
                        idPm: iDPM,
                        newIdIndex: 'Nueva OT',
                        createdAt: new Date(),
                        progress: 0,
                        createdBy: userData
                    }
                    setExistingReport(existing)
                    setReportToSave(populatedReport)
                    setOpenConfirmDialog(true)
                } else {
                    const apiMessage = error.response?.data?.message
                    const formattedInfo = Array.isArray(apiMessage)
                        ? apiMessage.join(' | ')
                        : (apiMessage || error.message || 'Error desconocido.')

                    const errorDetails = {
                        code: error.response?.status || 'Cliente',
                        info: formattedInfo
                    }
                    setstatus({
                        status: 'error',
                        message: 'Ocurrió un error al crear el reporte manual.',
                        errorDetails,
                        title: 'Error de creación'
                    })
                }
                // Fin de propuesta
                // No navegamos en caso de error para que el usuario pueda corregir la información
            } finally {
                setOpenLoadingLogo(false)
            }
        }
    }

    const confirmRestoreReport = async () => {
        setOpenConfirmDialog(false)
        if (existingReport) {
            setOpenLoadingLogo(true)
            try {
                const payload = existingReport._id 
                    ? { _id: existingReport._id, deleted: false }
                    : { sapId: existingReport.sapId || sapId, deleted: false }

                await reportsRoutes.editReport(payload)
                if (refreshNumbers) {
                    await refreshNumbers()
                }
                const otNumber = existingReport.newIdIndex || existingReport.idIndex || existingReport.sapId || sapId || ''
                setstatus({
                    status: 'completed',
                    message: `Reporte ${otNumber} restaurado satisfactoriamente.`,
                    title: 'Éxito',
                    severity: 'success'
                })
                await getReports()
                if (getReportsOffline) {
                    await getReportsOffline()
                }
                navigate('/reports', { replace: true })
            } catch (err) {
                console.error('Error al restaurar el reporte:', err)
                setstatus({
                    status: 'error',
                    message: err.response?.data?.message || 'Error al restaurar la OT eliminada.',
                    severity: 'error',
                    title: 'Error de restauración'
                })
            } finally {
                setOpenLoadingLogo(false)
            }
        }
    }

    const confirmReplaceReport = async () => {
        setOpenConfirmDialog(false)
        await saveReport(true)
    }

    const confirmRecreateReport = confirmReplaceReport

    const editReport = async () => {
        if (!machineSelected || machineSelected === 'init') {
            setstatus({
                status: 'error',
                message: 'Debe seleccionar una máquina.',
                severity: 'warning',
                title: 'Validación'
            })
            return
        }

        if (!date) {
            setstatus({
                status: 'error',
                message: 'Debe seleccionar la fecha de inicio programado.',
                severity: 'warning',
                title: 'Validación'
            })
            return
        }

        let machineObj
        try {
            machineObj = JSON.parse(machineSelected)
        } catch (e) {
            setstatus({
                status: 'error',
                message: 'Error al procesar la máquina seleccionada.',
                severity: 'error'
            })
            return
        }

        const report = {
            updatedBy: localStorage.getItem('_id'),
            state: 'Asignar',
            datePrev: Date.parse(date),
            guide: pauta,
            reportType: reportType,
            machine: machineObj.equid,
            site: idObra
        }

        if (!report.machine || (!report.guide || report.guide === 'Selección no cuenta con pautas.') || (!report.reportType || report.reportType === 'Seleccione...')) {
            setstatus({
                status: 'error',
                message: 'Falta información obligatoria para editar el reporte.',
                severity: 'warning',
                title: 'Información incompleta'
            })
        } else {
            setOpenLoadingLogo(true)
            try {
                const reportState = await reportsRoutes.editReport(report)
                if (reportState) {
                    setstatus({
                        status: 'completed',
                        message: `Reporte ${reportState.data?.idIndex || ''} editado satisfactoriamente.`,
                        title: 'Éxito',
                        severity: 'success'
                    })
                    await getReports()
                    if (getReportsOffline) {
                        await getReportsOffline()
                    }
                    navigate(-1)
                }
            } catch (error) {
                console.error('Error editing report:', error)
                const apiMessage = error.response?.data?.message
                const formattedInfo = Array.isArray(apiMessage)
                    ? apiMessage.join(' | ')
                    : (apiMessage || error.message || 'Error desconocido.')

                const errorDetails = {
                    code: error.response?.status || 'Cliente',
                    info: formattedInfo
                }
                setstatus({
                    status: 'error',
                    message: 'Ocurrió un error al editar el reporte.',
                    errorDetails,
                    title: 'Error de edición'
                })
            } finally {
                setOpenLoadingLogo(false)
            }
        }
    }

    useEffect(() => {
        if (!idObra) {
            const isUniversalUser = isUniversalInspector || isUniversalChief
            const isRegularUser = !admin && !isSuperAdmin && !isUniversalUser

            if (isRegularUser || isUniversalUser) {
                const isSiteSelectedValid =
                    siteSelected &&
                    siteSelected.idObra !== 'none' &&
                    siteSelected._id !== 'all' &&
                    siteSelected._id !== 'universal'

                if (isSiteSelectedValid) {
                    const initialIdObra = siteSelected.idObra || siteSelected.idobra || siteSelected._id
                    console.log('[CreateReport] Initializing idObra from siteSelected:', initialIdObra)
                    setIdObra(initialIdObra)
                } else if (userData && userData.obras && userData.obras.length > 0) {
                    const firstSite = userData.obras[0]
                    const initialIdObra =
                        typeof firstSite === 'object' && firstSite !== null
                            ? firstSite.idObra || firstSite.idobra || firstSite._id
                            : firstSite
                    console.log('[CreateReport] Initializing idObra from userData.obras[0]:', initialIdObra)
                    setIdObra(initialIdObra)
                }
            }
        }

        formatDateToDay()
    }, [admin, isSuperAdmin, isUniversalInspector, isUniversalChief, userData, idObra, siteSelected])

    const formatDateToDay = () => {
        const dtToday = new Date(Date.now())

        let month = dtToday.getMonth() + 1
        let day = dtToday.getDate()
        const year = dtToday.getFullYear()

        if (month < 10) { month = '0' + month.toString() }
        if (day < 10) { day = '0' + day.toString() }

        const minDate = year + '-' + month + '-' + day
        setToDay(minDate)
    }

    const deleteReport = async () => {
        if (id) {
            if (confirm('Se borrará el reporte permanentemente. ¿Desea continuar?')) {
                setOpenLoadingLogo(true)
                try {
                    const reportId = JSON.parse(id)._id
                    await reportsRoutes.deleteReport(reportId)
                    setstatus({
                        status: 'completed',
                        message: 'Reporte eliminado satisfactoriamente.',
                        title: 'Éxito',
                        severity: 'success'
                    })
                    await getReports()
                    if (getReportsOffline) {
                        await getReportsOffline()
                    }
                    navigate(-1)
                } catch (error) {
                    console.error('Error deleting report:', error)
                    const errorDetails = {
                        code: error.response?.status || 'Cliente',
                        info: error.response?.data?.message || error.message || 'Error desconocido.'
                    }
                    setstatus({
                        status: 'error',
                        message: 'Ocurrió un error al eliminar el reporte.',
                        errorDetails,
                        title: 'Error de eliminación'
                    })
                } finally {
                    setOpenLoadingLogo(false)
                }
            }
        }
    }

    const selectTruck = (e) => {
        console.log('selectTruck called with:', e)
        const model = typeof e === 'string' ? e : e.modelo
        if (model !== machineModel) {
            setMachineModel(model || '')

            const relatedMachines = allMachinesAtSite.filter(m => m.model === model)
            setMaquinas(relatedMachines)

            setMachineSelected('init')
            setPautas([])
            setPauta('')
            setIDPM('')
            setHourMeter('')
            setEqID('')
        }

        if (e.machine) {
            const selectedMachine = e.machine
            setMachineSelected(JSON.stringify(selectedMachine))
            setEqID(selectedMachine.equid || selectedMachine.equID)
            setHourMeter(segundosHoras(selectedMachine.hourMeter))
            if (!hasSiteSelector) {
                setIdObra(selectedMachine.idobra || selectedMachine.idObra)
            }
        }

        setDisableMaquinas(false)
        setOpenSelectionMachinery(false)
    }

    const buttonText = useMemo(() => {
        if (machineSelected && machineSelected !== 'init') {
            try {
                const m = JSON.parse(machineSelected)
                return `${m.equ} / ${m.brand} ${m.model}`
            } catch (e) {
                return machineModel || 'Seleccione'
            }
        }
        return machineModel.length === 0 ? 'Seleccione' : machineModel
    }, [machineSelected, machineModel])

    return (
        <Box height='100%'>
            <Grid className={'root'} container spacing={0}>
                <Grid className={'pageContainer'} item xs={12}>
                    <Card elevation={0} className={'pageCard'} >
                        <Grid container alignItems='center' justifyContent='center'>
                            <div className='navigation-bar' /* style={{width: '100%', textAlign: 'left' }} */>
                                <div style={{ width: '100%', textAlign: 'left', color: '#333', backgroundColor: '#fff', borderRadius: 20 }}>
                                    <Toolbar style={{ paddingLeft: 0, backgroundColor: '#F9F9F9', borderRadius: 10 }}>
                                        <IconButton onClick={() => setTimeout(() => {
                                            navigate(-1)
                                        }, 500)}>
                                            <ArrowBackIos style={{ color: '#333', fontSize: 16 }} />
                                        </IconButton>
                                        <h1 style={{ marginTop: 0, marginBottom: 0, fontSize: 16, flexGrow: 1 }}>
                                            {
                                                id && 'Ordenes / Editar orden de trabajo'
                                            }
                                            {
                                                !id && 'Ordenes / Crear nueva orden de trabajo'
                                            }
                                        </h1>
                                        {(isSuperAdmin) && (
                                            <Tooltip title="Ver reportes eliminados">
                                                <IconButton onClick={() => navigate('/reports/deleted')} sx={{ mr: 2 }}>
                                                    <HistoryIcon color="action" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Toolbar>
                                </div>
                            </div>
                            <div style={{ maxHeight: 'calc(100vh - 302px)', width: '100%', overflowY: 'auto' }}>
                                <Grid container >
                                    <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                                        {hasSiteSelector && <div style={{ width: '100%', padding: 10 }}>
                                            <FormControl >
                                                <p style={{ margin: 5 }}>Seleccionar Obra</p>
                                                <select
                                                    className={classes.inputsStyle}
                                                    name="userType"
                                                    id="userType"
                                                    style={{ width: '100%', minWidth: 250, height: 44, borderRadius: 10, fontSize: 20 }}
                                                    onChange={(e) => {
                                                        console.log('Site selected value:', e.target.value)
                                                        setIdObra(e.target.value)
                                                    }}
                                                    value={idObra}
                                                >
                                                    <option value="">Seleccione...</option>
                                                    {
                                                        (sitesToShow.length > 0) && sitesToShow.map((site, index) => {
                                                            return (
                                                                <option key={index} value={site.idObra}>{site.idObra} - {site.descripcion}</option>
                                                            )
                                                        })
                                                    }
                                                </select>
                                            </FormControl>
                                        </div>}
                                        {/* <div style={{width: '100%', padding: 10}}>
                                            <FormControl>
                                                <p style={{margin: 5}}>N° OT</p>
                                                <input
                                                    disabled
                                                    value={idIndex}
                                                    maxLength={12}
                                                    className={classes.inputsStyle}
                                                    type="text"
                                                    style={{ width: "100%", height: 44, borderRadius: 10, fontSize: 20 }} />
                                            </FormControl>
                                        </div> */}
                                        <div style={{ width: '100%', padding: 10 }}>
                                            <FormControl style={{ width: '40%' }}>
                                                <p style={{ margin: 5 }}>ID SAP / Motivo de Inspección</p>

                                                {!isUniversal && (
                                                    <input
                                                        disabled={!!selectedUniversalSap}
                                                        value={sapId}
                                                        onChange={(e) => setSapId(e.target.value)}
                                                        maxLength={12}
                                                        placeholder="Ej: 123456"
                                                        className={classes.inputsStyle}
                                                        type="text"
                                                        style={{
                                                            width: '100%',
                                                            height: 44,
                                                            borderRadius: 10,
                                                            fontSize: 20,
                                                            backgroundColor: selectedUniversalSap ? '#f0f0f0' : '#fff',
                                                            opacity: selectedUniversalSap ? 0.6 : 1,
                                                            transition: 'all 0.3s ease'
                                                        }}
                                                    />
                                                )}

                                                {(isUniversal || admin || isSuperAdmin) && (
                                                    <div style={{ display: 'flex', alignItems: 'center', marginTop: !isUniversal ? 10 : 0 }}>
                                                        <select
                                                            className={classes.inputsStyle}
                                                            style={{
                                                                flex: 1,
                                                                height: 44,
                                                                borderRadius: 10,
                                                                width: '20%',
                                                                fontSize: 20,
                                                                borderColor: selectedUniversalSap ? '#4CAF50' : '#ccc',
                                                                transition: 'border-color 0.3s ease'
                                                            }}
                                                            value={selectedUniversalSap}
                                                            onChange={(e) => setSelectedUniversalSap(e.target.value)}
                                                        >
                                                            <option value="">{isUniversal ? 'Seleccione...' : 'Seleccionar tipo...'}</option>
                                                            <option value="Recepción">Recepción</option>
                                                            <option value="Despacho">Despacho</option>
                                                            <option value="Rutinaria">Rutinaria</option>
                                                        </select>
                                                        {selectedUniversalSap && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setSelectedUniversalSap('')}
                                                                style={{
                                                                    marginLeft: 10,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: '#ff7066',
                                                                    colocr: 'white',
                                                                    border: 'none',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: 18,
                                                                    fontWeight: 'bold',
                                                                    boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
                                                                    transition: 'background-color 0.2s ease'
                                                                }}
                                                                title="Limpiar selección"
                                                            >
                                                                ✕
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </FormControl>
                                        </div>
                                        <div style={{ width: '100%', padding: 10 }}>
                                            <FormControl>
                                                <p style={{ margin: 5 }}>Fecha Inicio Programado</p>
                                                <input
                                                    value={date}
                                                    onChange={(e) => { setDate(e.target.value) }}
                                                    maxLength={12}
                                                    className={classes.inputsStyle}
                                                    type="date"
                                                    min={toDay}
                                                    style={{ width: '100%', height: 44, borderRadius: 10, fontSize: 20 }} />
                                            </FormControl>
                                        </div>
                                        <div style={{ width: '100%', padding: 10 }}>
                                            <FormControl>
                                                <p style={{ margin: 5 }}>Fecha Término Programado</p>
                                                <input
                                                    value={dateEnd}
                                                    onChange={(e) => { setDateEnd(e.target.value) }}
                                                    maxLength={12}
                                                    className={classes.inputsStyle}
                                                    type="date"
                                                    min={date}
                                                    disabled={!date}
                                                    style={{ width: '100%', height: 44, borderRadius: 10, fontSize: 20 }} />
                                            </FormControl>
                                        </div>
                                        <div style={{ width: '100%', padding: 10 }}>
                                            <p style={{ margin: 5 }}>Seleccionar tipo de reporte</p>
                                            <FormControl>
                                                <select
                                                    /* onBlur={()=>saveReportData(true)} */
                                                    className={classes.inputsStyle}
                                                    name="userType"
                                                    id="userType"
                                                    disabled={isUniversalInspector || isUniversalChief || isOperatorInspector}
                                                    style={{ width: '100%', minWidth: 250, height: 44, borderRadius: 10, fontSize: 20 }}
                                                    onChange={(e) => { setReportType(e.target.value); settingTypePauta(e.target.value) }}
                                                    value={reportType}
                                                >
                                                    <option value="Seleccione...">Seleccione...</option>
                                                    <option>Inspección</option>
                                                    <option>Mantención</option>
                                                    <option>Inspección y Mantención</option>
                                                </select>
                                            </FormControl>
                                        </div>
                                        <div style={{ width: '100%', padding: 10 }}>
                                            <FormControl >
                                                <p style={{ margin: 5 }}>Buscar Máquina (EQU o Modelo)</p>
                                                <button
                                                    onClick={(e) => { setOpenSelectionMachinery(true) }}
                                                    style={{
                                                        width: '100%',
                                                        minWidth: 250,
                                                        height: 44,
                                                        borderRadius: 10,
                                                        fontSize: 20,
                                                        textAlign: 'left',
                                                        backgroundColor: 'transparent',
                                                        cursor: 'pointer',
                                                        border: '1px #767676 solid'
                                                    }}>
                                                    {buttonText}
                                                </button>
                                                <SelectionPopover
                                                    id={'selection'}
                                                    open={openSelectionMachinery}
                                                    handleClose={() => { setOpenSelectionMachinery(false) }}
                                                    items={trucks}
                                                    selectItem={selectTruck}
                                                />
                                            </FormControl>
                                        </div>
                                    </Grid>
                                    <Grid item xl={6} lg={6} md={6} sm={12} xs={12} >
                                        <div style={{ width: '100%' }}>
                                            <FormControl >
                                                <p>Seleccionar máquina de obra</p>
                                                <select
                                                    className={classes.inputsStyle}
                                                    disabled={disableMaquinas}
                                                    name="userType"
                                                    id="userType"
                                                    style={{ width: '100%', minWidth: 250, height: 44, borderRadius: 10, fontSize: 20 }}
                                                    onChange={(e) => {
                                                        if (e.target.value === 'init') {
                                                            setHourMeter('')
                                                            setEqID('')
                                                            setMachineSelected('init')
                                                            setPauta('')
                                                            setPautas([])
                                                            setIDPM('')
                                                        } else {
                                                            const selectedMachine = JSON.parse(e.target.value)
                                                            setMachineSelected(e.target.value)
                                                            setEqID(selectedMachine.equid || selectedMachine.equID)
                                                            setHourMeter(segundosHoras(selectedMachine.hourMeter))
                                                            if (!hasSiteSelector) {
                                                                setIdObra(selectedMachine.idobra || selectedMachine.idObra)
                                                            }
                                                        }
                                                    }}
                                                    value={machineSelected}
                                                >
                                                    <option value={'init'}>Seleccione...</option>
                                                    {
                                                        [...filteredMaquinas].sort((a, b) => { return Number(a.equ) - Number(b.equ) }).map((maquina, index) => {
                                                            return (
                                                                <option key={index} value={JSON.stringify(maquina)}> {maquina.equ} / {maquina.type} {maquina.brand} - {maquina.model} </option>
                                                            )
                                                        })
                                                    }
                                                </select>
                                            </FormControl>
                                        </div>
                                        <div style={{ width: '100%', padding: 10 }}>
                                            <FormControl>
                                                <p>SPM</p>
                                                <input disabled value={iDPM || 'SIN SPM'} maxLength={12} className={classes.inputsStyle} type="text" style={{ width: '100%', height: 44, borderRadius: 10, fontSize: 20 }} />
                                            </FormControl>
                                        </div>
                                        <div style={{ width: '100%', padding: 10 }}>
                                            <FormControl>
                                                <p>Horas de operación</p>
                                                <input disabled value={hourMeter} maxLength={12} className={classes.inputsStyle} type="text" style={{ width: '100%', height: 44, borderRadius: 10, fontSize: 20 }} />
                                            </FormControl>
                                        </div>
                                        <div style={{ width: '100%', padding: 10 }}>
                                            <FormControl>
                                                <p>Equipo ID</p>
                                                <input disabled value={equID} maxLength={12} className={classes.inputsStyle} type="text" style={{ width: '100%', height: 44, borderRadius: 10, fontSize: 20 }} />
                                            </FormControl>
                                        </div>
                                        <br />
                                        <div style={{ width: '100%', padding: 10 }}>
                                            <FormControl >
                                                <p style={{ margin: 0 }}>Seleccionar Pauta  {(disablePautas && changed) && <b>ESPERE...</b>}</p>
                                                <select
                                                    disabled={(pautasParaMostrar.length === 0)}
                                                    className={classes.inputsStyle}
                                                    name="userType"
                                                    id="userType"
                                                    style={{ width: '100%', minWidth: 250, height: 44, borderRadius: 10, fontSize: 20 }}
                                                    onChange={(e) => { setPauta(e.target.value) }}
                                                    value={pauta}
                                                >
                                                    <option value="">Seleccione...</option>
                                                    {
                                                        (pautasParaMostrar.length > 0) && pautasParaMostrar.map((pauta, index) => {
                                                            if (!pauta.header || !pauta.header[1]) {
                                                                console.log('Skipping pauta with invalid header:', pauta)
                                                                return null
                                                            } else {
                                                                return (
                                                                    <option key={index} value={pauta.typepm}> {(pauta.idpm === 'SPM000787') ? 'Motor LMR' : 'Motor Estándar'} - {pauta.typepm} / {pauta.header[1] ? pauta.header[1].typeDataDesc : 'S/I'} </option>
                                                                )
                                                            }
                                                        })
                                                    }
                                                    {
                                                        (pautasParaMostrar.length === 0) && <option value=""> Selección no cuenta con pautas. </option>
                                                    }
                                                </select>
                                            </FormControl>
                                        </div>
                                        <div style={{ width: '100%', padding: 10 }}>
                                        </div>
                                    </Grid>
                                </Grid>
                            </div>
                        </Grid>
                        <Grid container>
                            <Grid item lg={6} md={6} sm={false} xs={false}>

                            </Grid>
                            <Grid item lg={6} md={6} sm={12} xs={12}>
                                <div style={{ width: '100%', paddingTop: 30, textAlign: 'center' }}>
                                    {
                                        id && <button onClick={() => editReport()} style={{ width: 189, height: 48, marginRight: 17, borderRadius: 23, fontSize: 20, color: '#fff', backgroundColor: '#BB2D2D', borderColor: '#BB2D2D' }}>
                                            Editar reporte
                                        </button>
                                    }
                                    {
                                        id && <button onClick={() => deleteReport()} style={{ width: 189, height: 48, marginRight: 17, borderRadius: 23, fontSize: 20, color: '#fff', backgroundColor: '#BB2D2D', borderColor: '#BB2D2D' }}>
                                            Borrar reporte
                                        </button>
                                    }
                                    {
                                        !id && <button onClick={() => saveReport()} style={{ width: 189, height: 48, marginRight: 17, borderRadius: 23, fontSize: 20, color: '#fff', backgroundColor: '#BB2D2D', borderColor: '#BB2D2D' }}>
                                            Agregar reporte
                                        </button>
                                    }

                                </div>
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>
            </Grid>
            <LoadingLogoDialog open={openLoadingLogo} />
            <ReportConfirmDialog
                open={openConfirmDialog}
                existingReport={existingReport}
                currentReport={reportToSave}
                mode="create"
                handleClose={() => setOpenConfirmDialog(false)}
                handleConfirm={confirmReplaceReport}
                handleRestore={confirmRestoreReport}
                handleReplace={confirmReplaceReport}
            />
        </Box>
    )
}

export default CreateReports
